import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ShoppingBag, Award } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { bgWithImageStyle } from "../utils/themeBg";
import { trackPurchase } from "../components/leovera/Analytics";
import { pctLabel } from "../utils/prDiscount";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("loading"); // loading | paid | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          clear();
          try {
            const firedKey = `leovera_purchase_tracked_${sessionId}`;
            if (!sessionStorage.getItem(firedKey)) {
              trackPurchase(data);
              sessionStorage.setItem(firedKey, "1");
            }
          } catch { /* ignore */ }
          return;
        }
        if (data.status === "expired") { setState("error"); return; }
        if (attempts >= 8) { setState("pending"); return; }
        setTimeout(poll, 2500);
      } catch {
        if (attempts >= 4) setState("error");
        else setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen text-white flex items-center justify-center px-4" style={bgWithImageStyle()} data-testid="checkout-success-page">
      <div className="w-full max-w-lg text-center">
        {state === "loading" && (
          <div data-testid="checkout-success-loading">
            <Loader2 className="w-10 h-10 text-[#C0C0C0] animate-spin mx-auto mb-6" />
            <p className="text-[#94A3B8] text-sm tracking-[0.2em] uppercase">Verifica del pagamento in corso…</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="checkout-success-paid" className="animate-in fade-in zoom-in-95 duration-700">
            <div className="relative mx-auto w-24 h-24 mb-8">
              <span className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
              <span className="relative flex items-center justify-center w-24 h-24 rounded-full bg-emerald-500/15 border border-emerald-400/50">
                <CheckCircle2 className="w-12 h-12 text-emerald-400" strokeWidth={1.5} />
              </span>
            </div>
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#C0C0C0]">LEOVERA Nutrition</span>
            <h1 className="mt-3 font-[Outfit] text-4xl sm:text-5xl font-light tracking-tight">Grazie per il tuo ordine!</h1>
            <p className="mt-4 text-[#94A3B8] text-sm leading-relaxed">
              {Number(info?.deposit_amount) > 0 ? (
                <>Abbiamo ricevuto il tuo anticipo{info?.customer_email ? <> · una conferma è stata inviata a <b className="text-[#E5E4E2]">{info.customer_email}</b></> : ""}. Il resto lo pagherai in contanti al corriere alla consegna.</>
              ) : (
                <>Il pagamento è stato completato con successo{info?.customer_email ? <> · una conferma è stata inviata a <b className="text-[#E5E4E2]">{info.customer_email}</b></> : ""}. Riceverai aggiornamenti sulla spedizione.</>
              )}
            </p>
            {info?.order_number && (
              <p className="mt-4 inline-block bg-[var(--lv-surface,#0A1121)] border border-[#C0C0C0]/30 rounded-full px-5 py-2 text-[12px] tracking-[0.2em] uppercase text-[#C0C0C0]" data-testid="checkout-order-number">
                Ordine <b className="text-white font-[Outfit]">{info.order_number}</b>
              </p>
            )}
            <div className="mt-8 bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-6 text-left" data-testid="checkout-order-summary">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] mb-4">Riepilogo ordine</p>
              {Array.isArray(info?.items) && info.items.length > 0 ? (
                <div className="space-y-2">
                  {info.items.map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3 text-sm" data-testid={`checkout-receipt-item-${idx}`}>
                      <span className="text-[#E5E4E2] truncate">
                        {it.quantity}× {it.product_name}
                        {it.variant && <span className="text-[#94A3B8]"> ({it.variant})</span>}
                      </span>
                      <span className="font-[Outfit] text-[#E5E4E2] shrink-0">€{(Number(it.unit_price) * Number(it.quantity)).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ) : info?.product_name ? (
                <p className="text-[#E5E4E2] text-sm mb-2">{info.product_name}</p>
              ) : null}

              <div className="mt-4 pt-3 border-t border-white/5 space-y-1.5 text-sm">
                {info?.subtotal != null && (
                  <div className="flex items-center justify-between text-[#94A3B8]">
                    <span>Subtotale</span><span className="font-[Outfit] text-[#E5E4E2]">€{Number(info.subtotal).toFixed(2)}</span>
                  </div>
                )}
                {Number(info?.discount_amount) > 0 && (
                  <div className="flex items-center justify-between text-emerald-300" data-testid="checkout-success-discount-row">
                    <span>{info.discount_code === "PR25" ? `Sconto PR a vita (${pctLabel(Math.round((Number(info.subtotal) > 0 ? (Number(info.discount_amount) / Number(info.subtotal)) * 100 : 25) * 10) / 10)}%)` : info.discount_code === "BENVENUTO10" ? `Sconto Benvenuto (${pctLabel(Math.round((Number(info.subtotal) > 0 ? (Number(info.discount_amount) / Number(info.subtotal)) * 100 : 10) * 10) / 10)}%)` : `Sconto ${info.discount_code ? `(${info.discount_code})` : ""}`}</span>
                    <span className="font-[Outfit]">−€{Number(info.discount_amount).toFixed(2)}</span>
                  </div>
                )}
                {info?.shipping_cost != null && (
                  <div className="flex items-center justify-between text-[#94A3B8]">
                    <span>Spedizione</span>
                    <span className="font-[Outfit] text-[#E5E4E2]">{Number(info.shipping_cost) === 0 ? "Gratuita" : `€${Number(info.shipping_cost).toFixed(2)}`}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between border-t border-white/10 pt-3 mt-3">
                <span className="text-[11px] text-[#94A3B8] uppercase tracking-[0.2em]">{Number(info?.deposit_amount) > 0 ? "Anticipo pagato" : "Totale pagato"}</span>
                <span className="font-[Outfit] text-2xl text-white" data-testid="checkout-receipt-total">
                  €{Number(Number(info?.deposit_amount) > 0 ? info.deposit_amount : ((info?.total ?? info?.amount_total) || 0)).toFixed(2)}
                </span>
              </div>
              {Number(info?.deposit_amount) > 0 && (
                <div className="flex items-center justify-between pt-1.5" data-testid="checkout-receipt-cash-due">
                  <span className="text-[11px] text-emerald-300 uppercase tracking-[0.2em]">Da pagare alla consegna</span>
                  <span className="font-[Outfit] text-lg text-emerald-300">€{Number(info.cash_due || 0).toFixed(2)}</span>
                </div>
              )}
              <p className="mt-3 inline-flex items-center gap-1.5 text-[10px] text-emerald-300 uppercase tracking-[0.2em]">
                <Award className="w-3 h-3" /> Punti Cashback in accredito alla consegna
              </p>
            </div>
            <Link to="/" data-testid="checkout-back-to-shop"
                  className="mt-8 inline-flex items-center gap-3 bg-[#C0C0C0] text-[#060A14] px-8 py-4 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white transition-colors">
              <ShoppingBag className="w-4 h-4" /> Torna allo Shop
            </Link>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="checkout-success-pending">
            <AlertTriangle className="w-10 h-10 text-amber-300 mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-[Outfit] text-3xl font-light">Pagamento in elaborazione</h1>
            <p className="mt-3 text-[#94A3B8] text-sm">Il pagamento è in corso di conferma. Controlla la tua email nei prossimi minuti.</p>
            <Link to="/" className="mt-8 inline-block text-[11px] tracking-[0.28em] uppercase text-[#C0C0C0] hover:text-white">Torna allo Shop</Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="checkout-success-error">
            <AlertTriangle className="w-10 h-10 text-red-300 mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-[Outfit] text-3xl font-light">Sessione non valida</h1>
            <p className="mt-3 text-[#94A3B8] text-sm">Non è stato possibile verificare il pagamento. Se hai completato l'acquisto, contatta l'assistenza.</p>
            <Link to="/" className="mt-8 inline-block text-[11px] tracking-[0.28em] uppercase text-[#C0C0C0] hover:text-white">Torna alla Home</Link>
          </div>
        )}
      </div>
    </div>
  );
}
